import { useTheme } from 'vuetify';

const STORAGE_KEY = 'jgi_theme';

export type ThemeMode = 'light' | 'dark';

const LIGHT_NAME = 'BLUE_THEME';
const DARK_NAME = 'DARK_BLUE_THEME';

export function useThemeMode() {
  const mode = useState<ThemeMode>('app-theme-mode', () => 'light');
  const isDark = computed(() => mode.value === 'dark');
  const theme = useTheme();
  const { runSwitch } = useAppSwitchOverlay();
  const { t } = useI18n();

  function vuetifyName(m: ThemeMode) {
    return m === 'dark' ? DARK_NAME : LIGHT_NAME;
  }

  function applyDom(m: ThemeMode) {
    if (!import.meta.client) return;
    document.documentElement.classList.toggle('jgi-dark', m === 'dark');
    document.documentElement.dataset.theme = m;
    document.documentElement.style.colorScheme = m === 'dark' ? 'dark' : 'light';
    localStorage.setItem(STORAGE_KEY, m);
  }

  function applyVuetify(m: ThemeMode) {
    const name = vuetifyName(m);
    theme.global.name.value = name;
    if (typeof theme.change === 'function') {
      try {
        theme.change(name);
      } catch {
        /* name already set */
      }
    }
  }

  function apply(m: ThemeMode = mode.value) {
    applyVuetify(m);
    applyDom(m);
  }

  function load() {
    if (!import.meta.client) return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') mode.value = raw;
    else mode.value = 'light';
    apply(mode.value);
  }

  function setMode(next: ThemeMode, opts?: { silent?: boolean }) {
    if (next === mode.value) return;
    if (opts?.silent || !import.meta.client) {
      mode.value = next;
      apply(next);
      return;
    }

    void runSwitch({
      kind: 'theme',
      title: t('switch.themeTitle'),
      subtitle: `${next === 'dark' ? t('switch.themeToDark') : t('switch.themeToLight')} · ${t('switch.themeSub')}`,
      apply: () => {
        mode.value = next;
        apply(next);
      },
    });
  }

  function toggle() {
    setMode(mode.value === 'dark' ? 'light' : 'dark');
  }

  return { mode, isDark, setMode, toggle, load, apply };
}
